import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const OrderDetailsModal = ({ order, onClose }) => {
  if (!order) return null;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return `${date?.getFullYear()}년 ${date?.getMonth() + 1}월 ${date?.getDate()}일`;
  };

  const formatPrice = (price) => {
    return `${price?.toLocaleString('ko-KR')}원`;
  };

  const getStatusColor = (status) => {
    const colors = {
      '주문 접수': 'bg-blue-100 text-blue-800',
      '픽업 예정': 'bg-yellow-100 text-yellow-800',
      '세탁 중': 'bg-purple-100 text-purple-800',
      '배송 예정': 'bg-orange-100 text-orange-800',
      '완료': 'bg-green-100 text-green-800'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-card shadow-xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-card px-6 py-4">
          <h2 className="text-xl font-bold text-foreground">주문 상세 정보</h2>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-muted-foreground transition-colors duration-150 hover:bg-muted hover:text-foreground"
            aria-label="닫기"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="space-y-6 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">주문번호</p>
              <p className="text-lg font-semibold text-foreground">{order?.orderNumber}</p>
            </div>
            <span className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${getStatusColor(order?.status)}`}>
              {order?.status}
            </span>
          </div>

          <div className="rounded-lg border border-border bg-muted/30 p-4">
            <h3 className="mb-3 flex items-center text-base font-semibold text-foreground">
              <Icon name="User" size={18} className="mr-2" />
              고객 정보
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">이름</span>
                <span className="text-sm font-medium text-foreground">{order?.customerName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">연락처</span>
                <span className="text-sm text-foreground">{order?.phone}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">주소</span>
                <span className="text-sm text-foreground text-right">{order?.address}</span>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-muted/30 p-4">
            <h3 className="mb-3 flex items-center text-base font-semibold text-foreground">
              <Icon name="Package" size={18} className="mr-2" />
              주문 항목
            </h3>
            <div className="space-y-3">
              {order?.items?.map((item, index) => (
                <div key={index} className="flex items-center justify-between rounded-lg border border-border bg-card p-3">
                  <div>
                    <p className="text-sm font-medium text-foreground">{item?.brand} - {item?.model}</p>
                    <p className="text-xs text-muted-foreground">{item?.serviceType}</p>
                    {item?.memo && (
                      <p className="mt-1 text-xs text-muted-foreground whitespace-pre-line">
                        메모: {item?.memo}
                      </p>
                    )}
                  </div>
                  <p className="text-sm font-semibold text-foreground">{formatPrice(item?.price)}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-border bg-muted/30 p-4">
            <h3 className="mb-3 flex items-center text-base font-semibold text-foreground">
              <Icon name="Truck" size={18} className="mr-2" />
              픽업/배송 정보
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">픽업 방법</span>
                <span className="text-sm text-foreground">{order?.pickupType}</span>
              </div>
              {order?.customInstructions && (
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">특이사항</span>
                  <span className="text-sm text-foreground text-right">{order?.customInstructions}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">카카오톡 알림</span>
                <span className="text-sm text-foreground">{order?.kakaoNotification ? '동의' : '미동의'}</span>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-muted/30 p-4">
            <h3 className="mb-3 flex items-center text-base font-semibold text-foreground">
              <Icon name="CreditCard" size={18} className="mr-2" />
              결제 정보
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">주문일</span>
                <span className="text-sm text-foreground">{formatDate(order?.orderDate)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">결제 상태</span>
                <span className="text-sm font-medium text-success">{order?.paymentStatus}</span>
              </div>
              <div className="flex justify-between border-t border-border pt-2">
                <span className="text-base font-semibold text-foreground">총 금액</span>
                <span className="text-base font-bold text-foreground">{formatPrice(order?.totalAmount)}</span>
              </div>
            </div>
          </div>

          {order?.internalNotes && (
            <div className="rounded-lg border border-border bg-muted/30 p-4">
              <h3 className="mb-3 flex items-center text-base font-semibold text-foreground">
                <Icon name="FileText" size={18} className="mr-2" />
                내부 메모
              </h3>
              <p className="text-sm text-muted-foreground">{order?.internalNotes}</p>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 border-t border-border bg-card px-6 py-4">
          <Button variant="outline" onClick={onClose} fullWidth>
            닫기
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsModal;